import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { Project } from '../../types';
import api from '../../services/api';

interface ProjectAssignment {
  id: string;
  contractorId: string;
  projectId: string;
  monthlyBudgetedHours: number | null;
  projectHourlyRate: number | null;
  contractor: {
    id: string;
    name: string;
    hourlyRate: number;
  };
}

interface ContractorHoursData {
  contractorId: string;
  contractorName: string;
  hourlyRate: number;
  projectedHours: number;
  actualHours: number;
  difference: number;
  status: 'Under' | 'On Target' | 'Over';
}

interface ProjectContractorICVSectionProps {
  project: Project;
}

const ProjectContractorICVSection: React.FC<ProjectContractorICVSectionProps> = ({ project }) => {
  const [assignments, setAssignments] = useState<ProjectAssignment[]>([]);
  const [contractors, setContractors] = useState<any[]>([]);
  const [contractorHoursData, setContractorHoursData] = useState<ContractorHoursData[]>([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [newContractorForm, setNewContractorForm] = useState({
    name: '',
    hourlyRate: '',
  });
  const [assignForm, setAssignForm] = useState({
    contractorId: '',
    projectedHours: '',
  });

  useEffect(() => {
    const loadProjectData = async () => {
      try {
        setLoading(true);
        await Promise.all([
          fetchAssignments(),
          fetchContractors(),
        ]);
      } finally {
        setLoading(false);
      }
    };

    loadProjectData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchAssignments(),
        fetchContractors(),
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignments = async () => {
    try {
      const response = await api.get(`/project-assignments?projectId=${project.id}`);
      setAssignments(response.data || []);
      await fetchContractorHours(response.data || []);
    } catch (error: any) {
      console.error('Error fetching assignments:', error);
    }
  };

  const fetchContractors = async () => {
    try {
      const response = await api.get('/contractors');
      setContractors(response.data || []);
    } catch (error: any) {
      console.error('Error fetching contractors:', error);
    }
  };

  const fetchContractorHours = async (assignmentsList: ProjectAssignment[]) => {
    try {
      const hoursDataPromises = assignmentsList.map(async (assignment) => {
        try {
          const timeEntriesResponse = await api.get(
            `/time-entries?projectId=${project.id}&contractorId=${assignment.contractorId}`
          );
          const timeEntries = timeEntriesResponse.data || [];
          const actualHours = timeEntries.reduce((sum: number, entry: any) => sum + (entry.hoursWorked || 0), 0);
          const projectedHours = assignment.monthlyBudgetedHours || 0;
          const difference = actualHours - projectedHours;
          
          let status: 'Under' | 'On Target' | 'Over' = 'On Target';
          if (actualHours > projectedHours) {
            status = 'Over';
          } else if (actualHours < projectedHours) {
            status = 'Under';
          }

          return {
            contractorId: assignment.contractorId,
            contractorName: assignment.contractor.name,
            hourlyRate: assignment.projectHourlyRate || assignment.contractor.hourlyRate,
            projectedHours,
            actualHours,
            difference,
            status,
          };
        } catch (error) {
          console.error(`Error fetching hours for contractor ${assignment.contractorId}:`, error);
          return {
            contractorId: assignment.contractorId,
            contractorName: assignment.contractor.name,
            hourlyRate: assignment.projectHourlyRate || assignment.contractor.hourlyRate,
            projectedHours: assignment.monthlyBudgetedHours || 0,
            actualHours: 0,
            difference: -(assignment.monthlyBudgetedHours || 0),
            status: 'Under' as const,
          };
        }
      });

      const hoursData = await Promise.all(hoursDataPromises);
      setContractorHoursData(hoursData);
    } catch (error) {
      console.error('Error fetching contractor hours:', error);
    }
  };

  const handleAddContractor = async () => {
    try {
      if (!newContractorForm.name || !newContractorForm.hourlyRate) {
        alert('Please fill in all required fields');
        return;
      }

      const contractorResponse = await api.post('/contractors', {
        name: newContractorForm.name,
        hourlyRate: parseFloat(newContractorForm.hourlyRate),
        contactEmail: '',
        isActive: true,
      });

      await api.post('/project-assignments', {
        contractorId: contractorResponse.data.id,
        projectId: project.id,
        monthlyBudgetedHours: null,
        projectHourlyRate: parseFloat(newContractorForm.hourlyRate),
      });

      setNewContractorForm({ name: '', hourlyRate: '' });
      setAddDialogOpen(false);
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to add contractor');
    }
  };

  const handleAssignContractor = async () => {
    try {
      if (!assignForm.contractorId) {
        alert('Please select a contractor');
        return;
      }

      await api.post('/project-assignments', {
        contractorId: assignForm.contractorId,
        projectId: project.id,
        monthlyBudgetedHours: assignForm.projectedHours ? parseFloat(assignForm.projectedHours) : null,
        projectHourlyRate: null,
      });

      setAssignForm({ contractorId: '', projectedHours: '' });
      setAssignDialogOpen(false);
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to assign contractor');
    }
  };

  const getStatusColor = (status: 'Under' | 'On Target' | 'Over') => {
    switch (status) {
      case 'Over':
        return 'error';
      case 'On Target':
        return 'success';
      case 'Under':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Contractor ICV Group</Typography>
        <Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setAssignDialogOpen(true)}
            sx={{ mr: 1 }}
          >
            Assign Contractor
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
          >
            Add Contractor
          </Button>
        </Box>
      </Box>

      {contractorHoursData.length === 0 ? (
        <Alert severity="info">
          No contractors assigned to this project. Click "Add Contractor" or "Assign Contractor" to get started.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Contractor Name</TableCell>
                <TableCell align="right">Hourly Rate</TableCell>
                <TableCell align="right">Projected Hours</TableCell>
                <TableCell align="right">Actual Hours</TableCell>
                <TableCell align="right">Difference</TableCell>
                <TableCell align="center">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {contractorHoursData.map((data) => (
                <TableRow key={data.contractorId}>
                  <TableCell>{data.contractorName}</TableCell>
                  <TableCell align="right">
                    ${data.hourlyRate.toFixed(2)}/hr
                  </TableCell>
                  <TableCell align="right">
                    {data.projectedHours > 0 ? data.projectedHours.toFixed(2) : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {data.actualHours.toFixed(2)}
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      color={data.difference > 0 ? 'error' : data.difference < 0 ? 'warning.main' : 'inherit'}
                    >
                      {data.difference > 0 ? '+' : ''}{data.difference.toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={data.status}
                      color={getStatusColor(data.status) as any}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add Contractor Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Contractor to Project</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Contractor Name *"
              value={newContractorForm.name}
              onChange={(e) => setNewContractorForm({ ...newContractorForm, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Hourly Rate *"
              type="number"
              value={newContractorForm.hourlyRate}
              onChange={(e) => setNewContractorForm({ ...newContractorForm, hourlyRate: e.target.value })}
              fullWidth
              required
              inputProps={{ step: '0.01', min: '0' }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddContractor}
            variant="contained"
            disabled={!newContractorForm.name || !newContractorForm.hourlyRate}
          >
            Add & Assign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Contractor Dialog */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Contractor to Project</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>Contractor</InputLabel>
              <Select
                value={assignForm.contractorId}
                label="Contractor"
                onChange={(e) => setAssignForm({ ...assignForm, contractorId: e.target.value })}
              >
                {contractors
                  .filter((c) => c.isActive && !assignments.find((a) => a.contractorId === c.id))
                  .map((contractor) => (
                    <MenuItem key={contractor.id} value={contractor.id}>
                      {contractor.name} (${contractor.hourlyRate.toFixed(2)}/hr)
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <TextField
              label="Projected Hours (optional)"
              type="number"
              value={assignForm.projectedHours}
              onChange={(e) => setAssignForm({ ...assignForm, projectedHours: e.target.value })}
              fullWidth
              inputProps={{ step: '0.01', min: '0' }}
              helperText="Expected hours for this project"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAssignContractor}
            variant="contained"
            disabled={!assignForm.contractorId}
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectContractorICVSection;


